import { useState, type SyntheticEvent } from 'react';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { redirectIfAuthenticated } from '@/lib/auth-guards';
import { api } from '@/api/client';
import { authClient } from '@/lib/auth-client';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { PasswordField, TextField } from '@/components/ui/Field';
import { AuthLayout } from '@/features/auth/AuthLayout';

export const Route = createFileRoute('/sign-up')({
  beforeLoad: () => redirectIfAuthenticated(),
  component: SignUpPage,
});

const MIN_PASSWORD_LENGTH = 10;

/** Four bars: length, then a mix of letters, numbers and symbols. */
function passwordStrength(value: string): number {
  let score = 0;
  if (value.length >= MIN_PASSWORD_LENGTH) score += 1;
  if (value.length >= 14) score += 1;
  if (/[a-z]/.test(value) && /[A-Z]/.test(value)) score += 1;
  if (/[0-9]/.test(value) || /[^a-zA-Z0-9]/.test(value)) score += 1;
  return score;
}

function SignUpPage() {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [phone, setPhone] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  const tooShort = password.length > 0 && password.length < MIN_PASSWORD_LENGTH;

  async function onSubmit(event: SyntheticEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setError(null);
    setPending(true);
    const { error: signUpError } = await authClient.signUp.email({
      name,
      email,
      password,
      callbackURL: `${window.location.origin}/dashboard?verified=1`,
    });
    if (signUpError) {
      setPending(false);
      setError(
        signUpError.message ??
          'We could not create your account. Check your email address and try again.',
      );
      return;
    }
    // The number is optional. If saving it fails the account still exists; they can add it in Account.
    if (phone.trim()) {
      await api.put('/api/v1/me/profile', { body: { phone: phone.trim() } }).catch(() => undefined);
    }
    setPending(false);
    await navigate({ to: '/dashboard' });
  }

  return (
    <AuthLayout
      title="Create your account"
      description="Practice tests, band estimates and feedback."
      footer={
        <>
          Already have an account?{' '}
          <Link to="/sign-in" className="font-bold text-primary hover:underline">
            Log in
          </Link>
        </>
      }
    >
      <form className="grid gap-4" onSubmit={(event) => void onSubmit(event)}>
        {error ? <Alert tone="danger" title={error} /> : null}

        <TextField
          label="Your name"
          required
          autoComplete="name"
          value={name}
          onChange={(event) => {
            setName(event.target.value);
          }}
        />

        <TextField
          label="Email"
          type="email"
          required
          autoComplete="email"
          value={email}
          onChange={(event) => {
            setEmail(event.target.value);
          }}
          placeholder="you@example.com"
        />

        <TextField
          label="Mobile or WhatsApp number (optional)"
          type="tel"
          autoComplete="tel"
          inputMode="tel"
          value={phone}
          onChange={(event) => {
            setPhone(event.target.value);
          }}
        />

        <PasswordField
          label="Password"
          required
          minLength={MIN_PASSWORD_LENGTH}
          autoComplete="new-password"
          value={password}
          onChange={(event) => {
            setPassword(event.target.value);
          }}
          strength={passwordStrength(password)}
          error={
            tooShort ? `Use at least ${MIN_PASSWORD_LENGTH} characters. Add a few more.` : null
          }
          placeholder={`At least ${MIN_PASSWORD_LENGTH} characters`}
        />

        <Button type="submit" size="lg" fullWidth loading={pending} disabled={tooShort}>
          {pending ? 'Creating your account' : 'Create account'}
        </Button>
      </form>
    </AuthLayout>
  );
}
